import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { PAGINATE_BY } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { maybe } from "@saleor/misc";
import PageEventListPage from "@saleor/pageManage/components/PageEventListPage";
import { userOperatePageEventQuery } from "@saleor/pageManage/queries";
import {
  pageEventListUrl,
  PageEventListUrlDialog,
  PageEventListUrlQueryParams
} from "@saleor/pageManage/urls";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import React from "react";

import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
interface PageEventListProps {
  params: PageEventListUrlQueryParams;
}

const PageEventList: React.FC<PageEventListProps> = ({ params }) => {
  const { updateListSettings, settings } = useListSettings(
    ListViews.PAGEEVENT_LIST
  );
  const paginationState = createPaginationState(
    settings ? settings.rowNumber : PAGINATE_BY,
    params
  );
  const queryVariables = {
    ...paginationState,
    filter: getFilterVariables(params)
  };
  const { data, loading } = userOperatePageEventQuery({
    variables: queryVariables
  });

  const paginate = usePaginator();

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.userOperatePageEvent.pageInfo),
    paginationState,
    params
  );
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const shop = useShop();

  const currencySymbol = maybe(() => shop.defaultCurrency, "USD");
  const navigate = useNavigator();

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    cleanupFn: reset,
    createUrl: pageEventListUrl,
    getFilterQueryParam,
    navigate,
    params
  });
  const [openModal, closeModal] = createDialogActionHandlers<
    PageEventListUrlDialog,
    PageEventListUrlQueryParams
  >(navigate, pageEventListUrl, params);
  const handleTabChange = (tab: number) => {
    reset();
    navigate(
      pageEventListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };
  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    reset();
    navigate(pageEventListUrl());
  };
  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };
  return (
    <>
      <PageEventListPage
        pageEvent={maybe(() =>
          data.userOperatePageEvent.edges.map(edge => edge.node)
        )}
        toatlCount={maybe(() => data.userOperatePageEvent.totalCount, 0)}
        onAdd={null}
        disabled={loading}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        pageInfo={pageInfo}
        onRowClick={null}
        initialSearch={params.query || ""}
        onUpdateListSettings={updateListSettings}
        toolbar={""}
        toggle={toggle}
        toggleAll={toggleAll}
        isChecked={isSelected}
        selected={listElements.length}
        filterOpts={getFilterOpts(params)}
        currencySymbol={currencySymbol}
        onFilterChange={changeFilters}
        currentTab={currentTab}
        tabs={tabs.map(tab => tab.name)}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onSearchChange={handleSearchChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
      />

      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={maybe(() => tabs[currentTab - 1].name, "...")}
      />
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />
    </>
  );
};
export default PageEventList;
